'use client';

import { useTransition, type SyntheticEvent } from 'react';
import { CART_DATA_PRIVACY_STATUS, useCartCheckoutStore, useCartStore } from 'stores/cart';
import { startOrderPlaceholder } from '../../checkout/partials/startOrderPlaceholder';

interface UseCheckoutOrderOptions {
  /** DK with a legal sentence: the customer must tick the sentence before ordering. */
  isDataPrivacyRequired: boolean;
}

/** Busy-guarded order click; the cart store shares the busy flag between the desktop and the mobile order button. */
export function useCheckoutOrder({ isDataPrivacyRequired }: UseCheckoutOrderOptions) {
  const [isPending, startTransition] = useTransition();
  const isCartBusy = useCartStore(store => store.isBusy);
  const setCartBusy = useCartStore(store => store.setCartBusy);
  const dataPrivacyStatus = useCartCheckoutStore(store => store.dataPrivacyStatus);
  const rejectDataPrivacy = useCartCheckoutStore(store => store.rejectDataPrivacy);
  const isBusy = isPending || isCartBusy;

  const handleOrderClick = (event: SyntheticEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();

    if (isBusy) {
      return;
    }

    if (isDataPrivacyRequired && dataPrivacyStatus !== CART_DATA_PRIVACY_STATUS.ACCEPTED) {
      rejectDataPrivacy();

      return;
    }

    startTransition(async () => {
      setCartBusy(true);

      try {
        await startOrderPlaceholder();
      } finally {
        setCartBusy(false);
      }
    });
  };

  return { isBusy, handleOrderClick };
}
